import { Controller } from "@hotwired/stimulus"

// Displays grid search coverage on a Leaflet/OpenStreetMap map (free, no API key)
// Supports click-to-expand for suburb searches
export default class extends Controller {
  static targets = ["map", "expandPanel"]
  static values = {
    cells: Array,
    center: Object,
    radius: Number,
    searchId: Number,
    isSuburbSearch: Boolean,
    nearbySuburbsUrl: String,
    expandSearchUrl: String
  }

  connect() {
    this.loadLeaflet()
  }

  loadLeaflet() {
    // Check if already loaded
    if (window.L) {
      this.initMap()
      return
    }

    // Load Leaflet CSS
    if (!document.querySelector('link[href*="leaflet"]')) {
      const css = document.createElement('link')
      css.rel = 'stylesheet'
      css.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css'
      css.integrity = 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY='
      css.crossOrigin = ''
      document.head.appendChild(css)
    }

    // Check if script is already being loaded
    if (document.querySelector('script[src*="leaflet"]')) {
      const checkLoaded = setInterval(() => {
        if (window.L) {
          clearInterval(checkLoaded)
          this.initMap()
        }
      }, 100)
      return
    }

    // Load Leaflet JS
    const script = document.createElement('script')
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js'
    script.integrity = 'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo='
    script.crossOrigin = ''
    script.onload = () => this.initMap()
    document.head.appendChild(script)
  }

  initMap() {
    const center = this.centerValue
    if (!center || !center.lat || !center.lng) {
      console.warn("No center coordinates provided for coverage map")
      return
    }

    // Initialize map
    this.map = L.map(this.mapTarget).setView([center.lat, center.lng], this.calculateZoom())

    // Add OpenStreetMap tiles (free!)
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; <a href="https://openstreetmap.org/copyright">OpenStreetMap</a>'
    }).addTo(this.map)

    // Draw the overall search radius circle
    L.circle([center.lat, center.lng], {
      radius: this.radiusValue,
      fillColor: '#3b82f6',
      fillOpacity: 0.05,
      color: '#3b82f6',
      opacity: 0.3,
      weight: 2
    }).addTo(this.map)

    // Draw each cell
    this.cellsValue.forEach(cell => this.drawCell(cell))

    // Fit bounds to show all cells
    this.fitBounds()

    // Add click handler for suburb search expansion
    if (this.isSuburbSearchValue && this.nearbySuburbsUrlValue) {
      this.map.on('click', (e) => this.handleMapClick(e))
      this.clickMarker = null
    }
  }

  async handleMapClick(e) {
    const { lat, lng } = e.latlng

    // Remove previous click marker if exists
    if (this.clickMarker) {
      this.map.removeLayer(this.clickMarker)
    }

    // Add a temporary marker at click location
    this.clickMarker = L.marker([lat, lng], {
      icon: L.divIcon({
        className: 'click-marker',
        html: '<div style="background: #3b82f6; width: 12px; height: 12px; border-radius: 50%; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>',
        iconSize: [16, 16],
        iconAnchor: [8, 8]
      })
    }).addTo(this.map)

    // Fetch nearby suburbs
    try {
      const url = `${this.nearbySuburbsUrlValue}?lat=${lat}&lng=${lng}&radius_km=30`
      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      if (!response.ok) throw new Error('Failed to fetch nearby suburbs')

      const data = await response.json()
      this.showExpandPopup(lat, lng, data.suburbs)
    } catch (error) {
      console.error('Error fetching nearby suburbs:', error)
      this.showErrorPopup(lat, lng, 'Failed to load nearby suburbs')
    }
  }

  showExpandPopup(lat, lng, suburbs, currentRadius = 30) {
    if (this.expandPopup) {
      this.map.closePopup(this.expandPopup)
    }

    if (suburbs.length === 0) {
      // Try expanding radius if we haven't hit the max
      const radiusSteps = [30, 50, 100, 200, 500]
      const currentIndex = radiusSteps.indexOf(currentRadius)
      const nextRadius = radiusSteps[currentIndex + 1]

      if (nextRadius) {
        // Show "searching wider" message and auto-expand
        const content = `
          <div style="font-family: system-ui; font-size: 14px; min-width: 200px;">
            <strong>No suburbs within ${currentRadius}km</strong>
            <p style="color: #64748b; margin: 8px 0 0 0; font-size: 12px;">
              Searching wider (${nextRadius}km)...
            </p>
          </div>
        `
        this.expandPopup = L.popup()
          .setLatLng([lat, lng])
          .setContent(content)
          .openOn(this.map)

        // Auto-expand after a brief delay
        setTimeout(() => this.fetchWithRadius(lat, lng, nextRadius), 500)
        return
      }

      // We've tried all radii
      const content = `
        <div style="font-family: system-ui; font-size: 14px; min-width: 200px;">
          <strong>No additional suburbs found</strong>
          <p style="color: #64748b; margin: 8px 0 0 0; font-size: 12px;">
            All suburbs in this state are already included in this search.
          </p>
        </div>
      `
      this.expandPopup = L.popup()
        .setLatLng([lat, lng])
        .setContent(content)
        .openOn(this.map)
      return
    }

    // Build suburb selection list
    const suburbItems = suburbs.slice(0, 10).map(s => `
      <label style="display: flex; align-items: center; gap: 8px; padding: 4px 0; cursor: pointer;">
        <input type="checkbox" value="${s.id}" class="expand-suburb-checkbox" checked style="width: 16px; height: 16px;">
        <span style="flex: 1;">${s.name}</span>
        ${s.distance_km ? `<span style="color: #94a3b8; font-size: 11px;">${s.distance_km}km</span>` : ''}
      </label>
    `).join('')

    const content = `
      <div style="font-family: system-ui; font-size: 14px; min-width: 250px; max-width: 300px;">
        <strong style="display: block; margin-bottom: 8px;">Add nearby suburbs</strong>
        <div style="max-height: 200px; overflow-y: auto; border: 1px solid #e2e8f0; border-radius: 4px; padding: 8px; margin-bottom: 12px;">
          ${suburbItems}
        </div>
        <div style="display: flex; gap: 8px;">
          <button type="button" class="expand-add-btn" style="flex: 1; padding: 8px 12px; background: #3b82f6; color: white; border: none; border-radius: 4px; cursor: pointer; font-weight: 500;">
            Add Selected
          </button>
          <button type="button" class="expand-cancel-btn" style="padding: 8px 12px; background: #f1f5f9; color: #475569; border: none; border-radius: 4px; cursor: pointer;">
            Cancel
          </button>
        </div>
      </div>
    `

    this.expandPopup = L.popup({ maxWidth: 350, closeButton: true })
      .setLatLng([lat, lng])
      .setContent(content)
      .openOn(this.map)

    // Add event listeners after popup opens
    setTimeout(() => {
      const addBtn = document.querySelector('.expand-add-btn')
      const cancelBtn = document.querySelector('.expand-cancel-btn')

      if (addBtn) {
        addBtn.addEventListener('click', () => this.addSelectedSuburbs())
      }
      if (cancelBtn) {
        cancelBtn.addEventListener('click', () => {
          this.map.closePopup(this.expandPopup)
          if (this.clickMarker) {
            this.map.removeLayer(this.clickMarker)
          }
        })
      }
    }, 100)
  }

  showErrorPopup(lat, lng, message) {
    const content = `
      <div style="font-family: system-ui; font-size: 14px; color: #ef4444;">
        <strong>Error</strong>
        <p style="margin: 4px 0 0 0;">${message}</p>
      </div>
    `
    L.popup()
      .setLatLng([lat, lng])
      .setContent(content)
      .openOn(this.map)
  }

  async addSelectedSuburbs() {
    const checkboxes = document.querySelectorAll('.expand-suburb-checkbox:checked')
    const suburbIds = Array.from(checkboxes).map(cb => cb.value)

    if (suburbIds.length === 0) {
      alert('Please select at least one suburb')
      return
    }

    // Disable button and show loading
    const addBtn = document.querySelector('.expand-add-btn')
    if (addBtn) {
      addBtn.disabled = true
      addBtn.textContent = 'Adding...'
    }

    try {
      const response = await fetch(this.expandSearchUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ suburb_ids: suburbIds })
      })

      if (!response.ok) throw new Error('Failed to add suburbs')

      const data = await response.json()

      // Close popup and show success
      this.map.closePopup(this.expandPopup)
      if (this.clickMarker) {
        this.map.removeLayer(this.clickMarker)
      }

      // Reload the page to show updated results
      if (data.added_count > 0) {
        window.location.reload()
      } else {
        alert(data.message)
      }
    } catch (error) {
      console.error('Error adding suburbs:', error)
      alert('Failed to add suburbs. Please try again.')
      if (addBtn) {
        addBtn.disabled = false
        addBtn.textContent = 'Add Selected'
      }
    }
  }

  drawCell(cell) {
    const color = this.statusColor(cell.status)
    const opacity = cell.status === 'completed' ? 0.4 : 0.6

    // Use smaller radius for suburb markers (they're points, not areas)
    const displayRadius = cell.name ? Math.min(cell.radius, 3000) : cell.radius

    const circle = L.circle([cell.lat, cell.lng], {
      radius: displayRadius,
      fillColor: color,
      fillOpacity: opacity,
      color: color,
      opacity: 0.8,
      weight: 1
    }).addTo(this.map)

    // For suburb searches, show expand option; for grid searches, show basic info
    if (this.isSuburbSearchValue && cell.name) {
      circle.on('click', (e) => {
        L.DomEvent.stopPropagation(e) // Prevent map click from firing
        this.showSuburbPopup(cell, e.latlng)
      })
    } else {
      // Basic popup for grid searches
      const title = cell.name || `Cell ${cell.id}`
      const popupContent = `
        <div style="font-family: system-ui; font-size: 14px;">
          <strong>${title}</strong><br>
          Status: <span style="color: ${color}; font-weight: 600;">${cell.status}</span><br>
          ${cell.results_count !== null ? `Results: <strong>${cell.results_count}</strong> businesses<br>` : ''}
          ${cell.api_calls_count !== null && cell.api_calls_count > 0 ? `API Calls: <strong>${cell.api_calls_count}</strong> (${(cell.results_count / cell.api_calls_count).toFixed(1)} results/call)<br>` : ''}
          ${!cell.name ? `Location: ${cell.lat.toFixed(4)}, ${cell.lng.toFixed(4)}` : ''}
        </div>
      `
      circle.bindPopup(popupContent)
    }
  }

  showSuburbPopup(cell, latlng) {
    const color = this.statusColor(cell.status)
    const atLimit = cell.results_count >= 20

    const content = `
      <div style="font-family: system-ui; font-size: 14px; min-width: 200px;">
        <strong>${cell.name}</strong><br>
        Status: <span style="color: ${color}; font-weight: 600;">${cell.status}</span><br>
        ${cell.results_count !== null ? `
          Results: ${cell.results_count}${atLimit ? '<span style="color: #f59e0b; font-size: 11px;"> (API limit)</span>' : ''}<br>
        ` : ''}
        ${atLimit ? `
          <p style="color: #64748b; font-size: 12px; margin: 4px 0 8px 0;">
            This suburb hit Google's 20 result limit. Search nearby suburbs to find more businesses.
          </p>
        ` : ''}
        <hr style="margin: 8px 0; border: none; border-top: 1px solid #e2e8f0;">
        <button type="button" class="expand-from-suburb-btn"
                data-lat="${cell.lat}" data-lng="${cell.lng}"
                style="width: 100%; padding: 8px 12px; background: #3b82f6; color: white; border: none; border-radius: 4px; cursor: pointer; font-weight: 500; font-size: 13px;">
          Find nearby suburbs to add
        </button>
      </div>
    `

    const popup = L.popup()
      .setLatLng(latlng)
      .setContent(content)
      .openOn(this.map)

    // Add event listener after popup opens
    setTimeout(() => {
      const expandBtn = document.querySelector('.expand-from-suburb-btn')
      if (expandBtn) {
        expandBtn.addEventListener('click', () => {
          const lat = parseFloat(expandBtn.dataset.lat)
          const lng = parseFloat(expandBtn.dataset.lng)
          this.map.closePopup(popup)
          this.fetchAndShowNearbySuburbs(lat, lng)
        })
      }
    }, 100)
  }

  async fetchAndShowNearbySuburbs(lat, lng) {
    // Add a temporary marker at the location
    if (this.clickMarker) {
      this.map.removeLayer(this.clickMarker)
    }

    this.clickMarker = L.marker([lat, lng], {
      icon: L.divIcon({
        className: 'click-marker',
        html: '<div style="background: #3b82f6; width: 12px; height: 12px; border-radius: 50%; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>',
        iconSize: [16, 16],
        iconAnchor: [8, 8]
      })
    }).addTo(this.map)

    try {
      const url = `${this.nearbySuburbsUrlValue}?lat=${lat}&lng=${lng}&radius_km=30`
      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      if (!response.ok) throw new Error('Failed to fetch nearby suburbs')

      const data = await response.json()
      this.showExpandPopup(lat, lng, data.suburbs)
    } catch (error) {
      console.error('Error fetching nearby suburbs:', error)
      this.showErrorPopup(lat, lng, 'Failed to load nearby suburbs')
    }
  }

  // Fetch nearby suburbs with a specific radius (used for auto-expanding search)
  async fetchWithRadius(lat, lng, radiusKm) {
    try {
      const url = `${this.nearbySuburbsUrlValue}?lat=${lat}&lng=${lng}&radius_km=${radiusKm}`
      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      if (!response.ok) throw new Error('Failed to fetch nearby suburbs')

      const data = await response.json()
      this.showExpandPopup(lat, lng, data.suburbs, radiusKm)
    } catch (error) {
      console.error('Error fetching nearby suburbs:', error)
      this.showErrorPopup(lat, lng, 'Failed to load nearby suburbs')
    }
  }

  statusColor(status) {
    switch (status) {
      case 'completed': return '#22c55e'  // green
      case 'in_progress': return '#3b82f6' // blue
      case 'failed': return '#ef4444'      // red
      default: return '#94a3b8'            // slate (pending)
    }
  }

  calculateZoom() {
    const radius = this.radiusValue
    if (radius > 40000) return 9
    if (radius > 20000) return 10
    if (radius > 10000) return 11
    if (radius > 5000) return 12
    return 13
  }

  fitBounds() {
    const bounds = L.latLngBounds()
    const center = this.centerValue
    const radiusMeters = this.radiusValue

    // Calculate approximate lat/lng offset for the radius
    // 1 degree latitude ≈ 111km
    // 1 degree longitude ≈ 111km * cos(latitude)
    const latOffset = (radiusMeters / 1000) / 111
    const lngOffset = (radiusMeters / 1000) / (111 * Math.cos(center.lat * Math.PI / 180))

    // Include the full search radius circle by extending bounds to circle edges
    bounds.extend([center.lat + latOffset, center.lng + lngOffset]) // NE corner
    bounds.extend([center.lat - latOffset, center.lng - lngOffset]) // SW corner
    bounds.extend([center.lat + latOffset, center.lng - lngOffset]) // NW corner
    bounds.extend([center.lat - latOffset, center.lng + lngOffset]) // SE corner

    // Also include all cells (in case they extend beyond the circle)
    this.cellsValue.forEach(cell => {
      bounds.extend([cell.lat, cell.lng])
    })

    this.map.fitBounds(bounds, { padding: [50, 50] })
  }

  disconnect() {
    // Clean up map when controller disconnects
    if (this.map) {
      this.map.remove()
    }
  }
}
